// statTotals.js — DD-STAT-CONSOLIDATION Phase 0
// SINGLE SOURCE OF TRUTH for every stat-table TOTAL/AGGREGATE row.
//
// Totals stop being consumer-supplied: given the per-player stat rows a table
// already renders, this computes the aggregate row by SUMMING COUNTING COMPONENTS
// and RECOMPUTING RATE STATS FROM THOSE SUMMED COMPONENTS — never a naive average
// of per-row rates, never the (H + HR*3)/AB SLG approximation.
//
// The math mirrors the backend SSoT exactly:
//   backend/src/services/stats/stats_aggregation_formulas.py
//   (batting_avg, obp, slg, ops, era, whip). Any change here must change there too.
//
// Rounding (matches backend + statFormatUtils): AVG/OBP/SLG/OPS/WHIP → 3 dp; ERA → 2 dp.

// ---- component vocabulary the engine sums (canonical JSONB keys) ----
export const HITTING_COUNTING_KEYS = [
  'games_played', 'at_bats', 'runs', 'hits', 'doubles', 'triples', 'home_runs',
  'rbi', 'walks', 'strikeouts', 'stolen_bases', 'caught_stealing',
  'hit_by_pitch', 'sacrifice_flies', 'sacrifice_hits',
];
export const PITCHING_COUNTING_KEYS = [
  'games_played', 'games_started', 'wins', 'losses', 'saves', 'blown_saves',
  'holds', 'quality_starts', 'innings_pitched', 'hits_allowed', 'runs_allowed',
  'earned_runs', 'home_runs_allowed', 'walks_allowed', 'strikeouts_pitched',
];

const num = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const round = (v, dp) => {
  const f = 10 ** dp;
  return Math.round((v + Number.EPSILON) * f) / f;
};

// ---- rate recompute (mirror of stats_aggregation_formulas.py) ----
export const battingAvg = (hits, ab) => (ab > 0 ? round(hits / ab, 3) : 0);
export const obp = (hits, walks, hbp, ab, sf) => {
  const denom = ab + walks + hbp + sf;
  return denom > 0 ? round((hits + walks + hbp) / denom, 3) : 0;
};
export const slg = (hits, doubles, triples, hr, ab) => {
  if (ab <= 0) return 0;
  const singles = hits - doubles - triples - hr;
  const totalBases = singles + 2 * doubles + 3 * triples + 4 * hr;
  return round(totalBases / ab, 3);
};
export const ops = (obpVal, slgVal) => round(obpVal + slgVal, 3);
export const era = (er, ip) => (ip > 0 ? round((er * 9) / ip, 2) : 0);
export const whip = (ha, bba, ip) => (ip > 0 ? round((ha + bba) / ip, 3) : 0);

/**
 * Extract the component object for a row.
 * Accepts either a raw component object ({at_bats, hits, ...}) or a player row
 * ({ stats: { [window]: { hitting|pitching: {...} } } }).
 *
 * A caller may override extraction with `getComponents(row, statType)` when the
 * components live off the default `stats.[window].[statType]` path (e.g. Keeper
 * PROJECTED mode: `player.projected.stats.hitting`). This keeps ONE totals engine
 * — the source is configurable, the math is not. A row with genuinely absent
 * components yields {} and therefore contributes NOTHING to the sums (num()
 * treats every missing key as 0 additively), never a fabricated value.
 */
const extractComponents = (row, statType, window, getComponents) => {
  if (!row || typeof row !== 'object') return {};
  if (typeof getComponents === 'function') {
    const custom = getComponents(row, statType);
    return (custom && typeof custom === 'object') ? custom : {};
  }
  const block = row.stats?.[window]?.[statType];
  if (block && typeof block === 'object') return block;
  // Raw component object passed directly.
  return row;
};

/**
 * Compute an aggregate stat object from a list of rows.
 *
 * @param {Array} rows - player rows or raw component objects
 * @param {Object} opts
 *   - statType: 'hitting' | 'pitching'   (required)
 *   - window:   stats window to read when rows are player objects (default 'season')
 * @returns {Object} a canonical-shaped stat object: summed counting components +
 *   recomputed rate stats (batting_avg/obp/slg/ops OR era/whip). Values are RAW
 *   numbers; the column registry applies display formatting.
 */
export const computeTotals = (rows, { statType, window = 'season', getComponents } = {}) => {
  if (statType !== 'hitting' && statType !== 'pitching') {
    throw new Error(`computeTotals: statType must be 'hitting' or 'pitching', got '${statType}'`);
  }
  const keys = statType === 'hitting' ? HITTING_COUNTING_KEYS : PITCHING_COUNTING_KEYS;
  const totals = {};
  keys.forEach((k) => { totals[k] = 0; });

  (rows || []).forEach((row) => {
    const c = extractComponents(row, statType, window, getComponents);
    keys.forEach((k) => { totals[k] += num(c[k]); });
  });

  if (statType === 'hitting') {
    totals.batting_avg = battingAvg(totals.hits, totals.at_bats);
    totals.obp = obp(totals.hits, totals.walks, totals.hit_by_pitch, totals.at_bats, totals.sacrifice_flies);
    totals.slg = slg(totals.hits, totals.doubles, totals.triples, totals.home_runs, totals.at_bats);
    totals.ops = ops(totals.obp, totals.slg);
  } else {
    totals.era = era(totals.earned_runs, totals.innings_pitched);
    totals.whip = whip(totals.hits_allowed, totals.walks_allowed, totals.innings_pitched);
  }
  return totals;
};

/**
 * Wrap a computed totals object as a totals ROW the shared tables consume, shaped
 * like a player row so the SAME column render fns read it via stats[window][type].
 *
 * @param {Object} totals - output of computeTotals
 * @param {Object} opts - { statType, window='season', label='TOTAL', extra={} }
 */
export const asTotalsRow = (totals, { statType, window = 'season', label = 'TOTAL', extra = {} } = {}) => ({
  isTotalsRow: true,
  info: { full_name: label, first_name: label, last_name: '' },
  stats: { [window]: { [statType]: totals } },
  ...extra,
});

export default computeTotals;
